"use client";

import * as PortOne from "@portone/browser-sdk/v2";
import { BadgeCheck, LoaderCircle, Smartphone } from "lucide-react";
import { useState } from "react";

import {
  IDENTITY_PROVIDER_LABELS,
  type IdentityProvider,
} from "@/lib/identity-types";

const PENDING_IDENTITY_KEY = "lighthost.identity.pending";

type Props = {
  email: string;
  redirectPath?: string;
  purpose?: "REGISTRATION" | "ACCOUNT_VERIFICATION";
  emailVerified?: boolean;
  verified: boolean;
  availableProviders: IdentityProvider[];
  onVerified: (identityVerificationId: string) => void;
  onError: (message: string) => void;
};

type PreparedIdentity = {
  identityVerificationId: string;
  provider: IdentityProvider;
  purpose: "REGISTRATION" | "ACCOUNT_VERIFICATION";
  channelKey: string | null;
  customData: string;
  kcpWebSiteId: string | null;
};

const providerDescriptions: Record<IdentityProvider, string> = {
  KCP: "본인 명의 휴대폰과 문자 인증번호로 확인",
  KG_INICIS: "보유한 민간인증서를 이용해 간편하게 확인",
};

export default function IdentityVerificationButton({
  email,
  redirectPath = "/pass-auth/callback",
  purpose = "ACCOUNT_VERIFICATION",
  emailVerified = false,
  verified,
  availableProviders,
  onVerified,
  onError,
}: Props) {
  const [loadingProvider, setLoadingProvider] =
    useState<IdentityProvider | null>(null);

  async function handleVerification(provider: IdentityProvider) {
    if (loadingProvider || verified) return;
    if (purpose === "REGISTRATION" && !emailVerified) {
      onError("이메일 인증을 먼저 완료해주세요.");
      return;
    }

    setLoadingProvider(provider);
    onError("");

    try {
      const prepared = await fetch("/api/identity/prepare", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider, purpose, email }),
      });
      const preparedData = (await prepared
        .json()
        .catch(() => null)) as PreparedIdentity | null;

      if (!prepared.ok || !preparedData) {
        throw new Error(
          (preparedData as { message?: string } | null)?.message ??
            "본인인증 준비에 실패했습니다."
        );
      }

      const { identityVerificationId, customData } = preparedData;
      const storeId = process.env.NEXT_PUBLIC_PORTONE_STORE_ID;
      if (!storeId || !preparedData.channelKey) {
        throw new Error("본인인증 채널 설정이 누락되었습니다.");
      }

      sessionStorage.setItem(
        PENDING_IDENTITY_KEY,
        JSON.stringify({
          identityVerificationId,
          provider,
          purpose,
          ...(purpose === "REGISTRATION" ? { email } : {}),
        })
      );

      const response = await PortOne.requestIdentityVerification({
        storeId,
        channelKey: preparedData.channelKey,
        identityVerificationId,
        customData,
        redirectUrl: `${window.location.origin}${redirectPath}`,
        customer: { email },
        bypass:
          provider === "KCP"
            ? preparedData.kcpWebSiteId
              ? { kcpV2: { web_siteid: preparedData.kcpWebSiteId } }
              : undefined
            : { inicisUnified: { flgFixedUser: "N", FRGNDInfo: "N" } },
      });

      if (!response) throw new Error("본인인증 창이 닫혔습니다.");
      if (response.code) {
        throw new Error(response.message ?? "본인인증에 실패했습니다.");
      }
      if (response.identityVerificationId !== identityVerificationId) {
        throw new Error("본인인증 요청 번호가 일치하지 않습니다.");
      }

      const verifiedResponse = await fetch("/api/identity/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identityVerificationId }),
      });
      const verifiedData = await verifiedResponse.json().catch(() => null);
      if (!verifiedResponse.ok) {
        throw new Error(
          verifiedData?.message ?? "본인인증 확인에 실패했습니다."
        );
      }

      sessionStorage.removeItem(PENDING_IDENTITY_KEY);
      onVerified(identityVerificationId);
    } catch (error) {
      sessionStorage.removeItem(PENDING_IDENTITY_KEY);
      onError(
        error instanceof Error
          ? error.message
          : "본인인증 중 오류가 발생했습니다."
      );
    } finally {
      setLoadingProvider(null);
    }
  }

  if (verified) {
    return (
      <div className="flex items-center gap-4 border border-[#b9dfcf] bg-[#f0fbf6] px-4 py-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-[#047857] shadow-sm">
          <BadgeCheck className="h-5 w-5" aria-hidden="true" />
        </span>
        <span>
          <span className="block text-sm font-black text-[#075f49]">본인인증 완료</span>
          <span className="mt-1 block text-xs text-[#397663]">가입자 확인이 정상적으로 완료되었습니다.</span>
        </span>
      </div>
    );
  }

  return (
    <div className="divide-y divide-[#e5e1e9] border-y border-[#e5e1e9]">
      {(["KCP", "KG_INICIS"] as const).map((provider) => {
        const available = availableProviders.includes(provider);
        const loading = loadingProvider === provider;
        const Icon = provider === "KCP" ? Smartphone : BadgeCheck;

        return (
          <button
            key={provider}
            type="button"
            onClick={() => handleVerification(provider)}
            disabled={Boolean(loadingProvider) || verified || !available}
            className="group flex w-full items-center gap-4 px-2 py-5 text-left transition hover:bg-[#faf7ff] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#F4ECFF] text-[#7B3FD1] transition group-hover:bg-[#eadcff]">
              {loading ? (
                <LoaderCircle className="h-5 w-5 animate-spin" />
              ) : (
                <Icon className="h-5 w-5" />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-black text-[#302c35]">
                {IDENTITY_PROVIDER_LABELS[provider]}
              </span>
              <span className="mt-1 block text-sm text-[#77717e]">
                {providerDescriptions[provider]}
              </span>
            </span>
            <span className="shrink-0 text-xs font-black text-[#8F4FE8]">
              {loading ? "확인 중" : available ? "인증하기" : "설정 필요"}
            </span>
          </button>
        );
      })}
    </div>
  );
}
